import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Store, Package, Image as ImageIcon, Sparkles, PencilRuler, AlertOctagon, CheckCircle2, MessageSquareWarning, Ban, Archive } from "lucide-react";

const SECTIONS = [
  { key: "brands_pending", kind: "brand", title: "Brands Pending Approval", Icon: Store },
  { key: "products_pending", kind: "product", title: "Products Pending Review", Icon: Package },
  { key: "images_pending", kind: "image", title: "Images Pending Review", Icon: ImageIcon },
  { key: "showcase_requests", kind: "product", title: "Showcase Requests", Icon: Sparkles },
  { key: "changes_requested", kind: "product", title: "Changes Requested", Icon: PencilRuler },
  { key: "compliance_hold", kind: "product", title: "Items on Compliance Hold", Icon: AlertOctagon },
];

export default function AdminApprovalCentre() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("brands_pending");
  const [reasonFor, setReasonFor] = useState(null); // {kind,id,action,label}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/approval-centre").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const doAction = async (kind, id, action, reasonText = "") => {
    try { await api.post(`/admin/approval/${kind}/${id}/action`, { action, reason: reasonText }); toast.success("Done"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitReason = async () => {
    if (!reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try { await api.post(`/admin/approval/${reasonFor.kind}/${reasonFor.id}/action`, { action: reasonFor.action, reason: reason.trim() }); toast.success("Done"); setReasonFor(null); setReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const counts = data?.counts || {};
  const section = SECTIONS.find((s) => s.key === active);
  const items = data?.[active] || [];

  const ActionButtons = ({ kind, id, showApprove = true }) => (
    <div className="flex items-center gap-1.5 flex-wrap justify-end">
      {showApprove && <Button size="sm" onClick={() => doAction(kind, id, "approve")} className="rounded-full bg-forest hover:bg-earth text-cream h-8" data-testid={`approve-${id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>}
      <Button size="sm" variant="outline" onClick={() => { setReasonFor({ kind, id, action: "request_changes" }); setReason(""); }} className="rounded-full border-mustard text-earth h-8" data-testid={`changes-${id}`}><MessageSquareWarning className="w-3.5 h-3.5 mr-1" /> Request Changes</Button>
      <Button size="sm" variant="outline" onClick={() => doAction(kind, id, "compliance_hold", "Placed on compliance hold")} className="rounded-full border-red-400 text-red-600 h-8" data-testid={`hold-${id}`}><Ban className="w-3.5 h-3.5 mr-1" /> Hold</Button>
      <Button size="sm" variant="outline" onClick={() => doAction(kind, id, "archive")} className="rounded-full border-brown/40 text-brown h-8" data-testid={`archive-${id}`}><Archive className="w-3.5 h-3.5 mr-1" /> Archive</Button>
    </div>
  );

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight flex items-center gap-2"><ShieldCheck className="w-7 h-7" /> Approval Centre</h1>
        <p className="text-sm text-brown/70 mt-1">Everything awaiting your action, in one place. All actions are logged with your name and time.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        {SECTIONS.map((s) => (
          <button key={s.key} onClick={() => setActive(s.key)} data-testid={`ac-tab-${s.key}`} className={`text-left rounded-2xl p-4 border transition-colors ${active === s.key ? "bg-forest text-cream border-forest" : "bg-white border-sage/30 hover:border-forest"}`}>
            <div className="flex items-center justify-between"><span className="text-2xl font-heading">{counts[s.key] ?? 0}</span><s.Icon className="w-4 h-4 opacity-70" /></div>
            <p className={`text-xs font-500 mt-1 ${active === s.key ? "text-cream" : "text-forest"}`}>{s.title}</p>
          </button>
        ))}
      </div>

      <h2 className="font-heading text-xl text-forest mb-3 flex items-center gap-2"><section.Icon className="w-5 h-5" /> {section.title}</h2>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> :
        items.length === 0 ? <div className="text-center py-14 text-muted-foreground bg-white rounded-2xl soft-shadow"><CheckCircle2 className="w-9 h-9 mx-auto mb-2 text-sage" />Nothing pending here. </div> : (
        <div className="space-y-3" data-testid="ac-list">
          {active === "images_pending" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((im) => (
                <div key={im.id} className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid={`ac-item-${im.id}`}>
                  <div className="h-40 bg-sand"><img src={im.url} alt={im.slot} className="w-full h-full object-cover" /></div>
                  <div className="p-3">
                    <p className="text-sm font-500 text-forest truncate">{im.product_name}</p>
                    <p className="text-xs text-brown/60 mb-2">{im.brand_name} • {im.slot}</p>
                    <ActionButtons kind="image" id={im.id} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            items.map((it) => (
              <div key={it.id} className="bg-white rounded-2xl soft-shadow p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`ac-item-${it.id}`}>
                <div className="min-w-0">
                  <p className="font-500 text-forest">{it.brand_name || it.name}{it.name && it.brand_name ? ` — ${it.name}` : ""}</p>
                  <p className="text-xs text-brown/60">{section.kind === "brand" ? (it.portal_email || it.legal_name || "Brand profile") : `SKU ${it.sku || "—"} • ${it.status}`}</p>
                  {it.review_note && <p className="text-xs text-terracotta mt-0.5">Note: {it.review_note}</p>}
                </div>
                <ActionButtons kind={section.kind} id={it.id} />
              </div>
            ))
          )}
        </div>
      )}

      <Dialog open={!!reasonFor} onOpenChange={(o) => !o && setReasonFor(null)}>
        <DialogContent className="bg-cream max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">Request Changes</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Reason (visible to the brand) *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="rounded-xl bg-white" placeholder="Explain what needs to change…" data-testid="ac-reason-input" />
          </div>
          <DialogFooter><Button onClick={submitReason} disabled={busy} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="ac-reason-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
